import { registerPlugin, PluginListenerHandle } from '@capacitor/core';

export interface PermissionStatus {
  notifications: 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';
}

/**
 * Native Foreground Service plugin for keeping audio recordings alive
 * when the screen is locked or the app is in the background.
 *
 * - **Android**: Starts/stops a Foreground Service with a persistent
 *   "Recording in progress" notification and `foregroundServiceType=microphone`.
 * - **iOS**: No-op — background audio is handled via `UIBackgroundModes: audio`
 *   and the `AVAudioSession` configuration in `AppDelegate.swift`.
 */
export interface RecordingServicePlugin {
  /**
   * Start the native foreground service.
   * On Android, this shows a persistent notification and prevents the OS
   * from killing the microphone when the screen is off.
   */
  startForeground(): Promise<void>;

  /**
   * Stop the native foreground service.
   * Call this when the recording is stopped to remove the notification.
   */
  stopForeground(): Promise<void>;

  /**
   * Check current notification permission status.
   * Required on Android 13+ (API 33) to show the foreground service notification.
   */
  checkPermissions(): Promise<PermissionStatus>;

  /**
   * Request notification permission from the user.
   * On Android 13+, this triggers the system permission dialog.
   * On older versions and iOS, this resolves immediately as granted.
   */
  requestPermissions(): Promise<PermissionStatus>;

  /**
   * Subscribe to native recording interruption events (phone call, audio focus loss).
   * The native layer fires this when the OS interrupts the microphone session.
   */
  addListener(
    event: 'recordingInterrupted',
    listener: (data: { reason: string }) => void,
  ): Promise<PluginListenerHandle>;
}

export const RecordingService = registerPlugin<RecordingServicePlugin>(
  'RecordingService',
  {
    // On web/unsupported platforms, these are no-ops
    web: () => ({
      startForeground: () => Promise.resolve(),
      stopForeground: () => Promise.resolve(),
      checkPermissions: () =>
        Promise.resolve({ notifications: 'granted' as const }),
      requestPermissions: () =>
        Promise.resolve({ notifications: 'granted' as const }),
      addListener: (_event: string, _cb: (data: { reason: string }) => void) =>
        Promise.resolve({ remove: () => Promise.resolve() }),
    }),
  },
);
